import { useState, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, onSnapshot, collection, query, where, Timestamp, addDoc, getDocs, updateDoc } from 'firebase/firestore';
import { auth, db, getImpersonationData } from '../lib/firebase';

export interface AuthUser {
  uid: string;
  email: string | null;
  isAdmin: boolean;
}

export interface ImpersonationData {
  adminUid: string;
  adminEmail: string;
  targetUserId: string;
  targetEmail: string;
  startedAt: string;
}

interface BroadcastMessage {
  id: string;
  message: string;
  createdAt: Date;
  createdBy: string;
  active: boolean;
}

interface SeenMessage {
  messageId: string;
  userId: string;
  seenAt: Date;
}

export const useAuth = () => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [unreadBroadcast, setUnreadBroadcast] = useState<BroadcastMessage | null>(null);
  const [impersonationData, setImpersonationData] = useState<ImpersonationData | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        // Check if admin is impersonating a user
        const impersonation = getImpersonationData();

        if (impersonation) {
          // Admin is impersonating - show target user's data
          setImpersonationData(impersonation);
          const targetUserDocRef = doc(db, 'users', impersonation.targetUserId);

          const unsubscribeUser = onSnapshot(targetUserDocRef, async (doc) => {
            if (doc.exists()) {
              const userData = doc.data();
              const authUser = {
                uid: impersonation.targetUserId,
                email: impersonation.targetEmail,
                isAdmin: false // Show as regular user
              };
              setUser(authUser);

              // Check for unread broadcast messages for target user
              const broadcastQuery = query(
                collection(db, 'broadcastMessages'),
                where('active', '==', true)
              );
              const broadcastSnapshot = await getDocs(broadcastQuery);
              const broadcasts = broadcastSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                createdAt: doc.data().createdAt?.toDate()
              })) as BroadcastMessage[];

              if (broadcasts.length > 0) {
                const seenQuery = query(
                  collection(db, 'seenMessages'),
                  where('userId', '==', impersonation.targetUserId)
                );
                const seenSnapshot = await getDocs(seenQuery);
                const seenMessages = seenSnapshot.docs.map(doc => doc.data()) as SeenMessage[];

                const unread = broadcasts.find(broadcast =>
                  !seenMessages.some(seen => seen.messageId === broadcast.id)
                );

                if (unread) {
                  setUnreadBroadcast(unread);
                }
              }
            }
            setLoading(false);
          }, (error) => {
            console.error('Error fetching target user data:', error);
            setLoading(false);
          });

          return () => unsubscribeUser();
        }

        // Normal authentication (no impersonation)
        const userDocRef = doc(db, 'users', firebaseUser.uid);

        // Update last login timestamp
        try {
          await updateDoc(userDocRef, {
            lastLogin: Timestamp.now()
          });
        } catch (error) {
          console.error('Error updating last login:', error);
        }

        const unsubscribeUser = onSnapshot(userDocRef, async (doc) => {
          if (doc.exists()) {
            const userData = doc.data();
            const authUser = {
              uid: firebaseUser.uid,
              email: firebaseUser.email,
              isAdmin: userData?.isAdmin || false
            };
            setUser(authUser);

            // Check for unread broadcast messages
            const broadcastQuery = query(
              collection(db, 'broadcastMessages'),
              where('active', '==', true)
            );
            const broadcastSnapshot = await getDocs(broadcastQuery);
            const broadcasts = broadcastSnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data(),
              createdAt: doc.data().createdAt?.toDate()
            })) as BroadcastMessage[];

            if (broadcasts.length > 0) {
              // Get seen messages for this user
              const seenQuery = query(
                collection(db, 'seenMessages'),
                where('userId', '==', firebaseUser.uid)
              );
              const seenSnapshot = await getDocs(seenQuery);
              const seenMessages = seenSnapshot.docs.map(doc => doc.data()) as SeenMessage[];

              // Find the first unread active broadcast
              const unread = broadcasts.find(broadcast => 
                !seenMessages.some(seen => seen.messageId === broadcast.id)
              );

              if (unread) {
                setUnreadBroadcast(unread);
              }
            }
          } else {
            setUser({
              uid: firebaseUser.uid,
              email: firebaseUser.email,
              isAdmin: false
            });
          }
          setLoading(false);
        }, (error) => {
          console.error('Error fetching user data:', error);
          setLoading(false);
        });

        return () => unsubscribeUser();
      } else {
        setUser(null);
        setUnreadBroadcast(null);
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const markBroadcastAsSeen = async (messageId: string) => {
    if (!user) return;

    try {
      await addDoc(collection(db, 'seenMessages'), {
        messageId,
        userId: user.uid,
        seenAt: Timestamp.now()
      });
      setUnreadBroadcast(null);
    } catch (error) {
      console.error('Error marking broadcast as seen:', error);
    }
  };

  return { user, loading, unreadBroadcast, markBroadcastAsSeen, impersonationData };
}